import { addressHasAnyDetail, contactHasAnyDetail, t } from "./customerFormBaseline.js"

/**
 * True when the new-order form has user-entered data worth protecting on leave.
 * Ignores auto-filled defaults (drop-off location, bill-same-as-ship, soft mirrors alone).
 * @param {{
 *   contact: Record<string, unknown>,
 *   delivery: Record<string, unknown>,
 *   shipping: Record<string, unknown>,
 *   billing: Record<string, unknown>,
 *   dropoff: Record<string, unknown>,
 *   processing: Record<string, unknown>,
 *   signatureBlob?: Blob | null,
 *   todayYyyyMmDd: string,
 * }} args
 */
export function isNewOrderFormDirty({
    contact,
    delivery,
    shipping,
    billing,
    dropoff,
    processing,
    signatureBlob = null,
    todayYyyyMmDd,
}) {
    if (contact.customerId != null) {
        return true
    }
    if (contactHasAnyDetail(contact)) {
        return true
    }
    if (Boolean(contact.termsAccepted) || Boolean(contact.subscribe_newsletter)) {
        return true
    }
    if (signatureBlob instanceof Blob) {
        return true
    }

    if (String(delivery.method ?? "ship") !== "ship") {
        return true
    }
    if (delivery.pickupLocationId != null) {
        return true
    }
    if (Boolean(delivery.holdForPickup)) {
        return true
    }

    if (shipping.shippingCustomerId != null || addressHasAnyDetail(shipping)) {
        return true
    }

    if (billing.billingCustomerId != null) {
        return true
    }
    if (!billing.useShippingAddress && addressHasAnyDetail(billing)) {
        return true
    }

    if (t(dropoff.date) !== "" && t(dropoff.date) !== t(todayYyyyMmDd)) {
        return true
    }

    if (t(processing.totalWeight) !== "" || t(processing.containers) !== "") {
        return true
    }
    const options = processing.options ?? {}
    if (Object.values(options).some(Boolean)) {
        return true
    }
    for (const row of processing.instructions ?? []) {
        if (row?.speciesId != null || row?.processId != null) {
            return true
        }
        if (Number(row?.quantity) > 0) {
            return true
        }
        if (t(row?.additionalNotes) !== "") {
            return true
        }
    }

    return false
}

function snapshotAddressFields(v) {
    return {
        firstName: t(v.firstName),
        lastName: t(v.lastName),
        companyName: t(v.companyName),
        email: t(v.email),
        country: t(v.country),
        province: t(v.province),
        city: t(v.city),
        streetAddress: t(v.streetAddress),
        postalCode: t(v.postalCode),
        phone: t(v.phone),
    }
}

function normalizeOptions(options) {
    /** @type {Record<string, boolean>} */
    const out = {}
    for (const key of Object.keys(options ?? {}).sort()) {
        if (Boolean(options[key])) {
            out[String(key)] = true
        }
    }
    return out
}

/**
 * Serializable (+ File/Blob refs) snapshot for edit-order dirty detection.
 * @param {{
 *   contact: Record<string, unknown>,
 *   delivery: Record<string, unknown>,
 *   shipping: Record<string, unknown>,
 *   billing: Record<string, unknown>,
 *   dropoff: Record<string, unknown>,
 *   processing: Record<string, unknown>,
 *   signatureBlob?: Blob | null,
 * }} args
 */
export function snapshotOrderFormDirtyState({
    contact,
    delivery,
    shipping,
    billing,
    dropoff,
    processing,
    signatureBlob = null,
}) {
    return {
        contact: {
            customerId: contact.customerId ?? null,
            firstName: t(contact.firstName),
            lastName: t(contact.lastName),
            email: t(contact.email),
            phone: t(contact.phone),
            companyName: t(contact.companyName),
            contact_method: t(contact.contact_method) || "email",
            is_staff: Boolean(contact.is_staff),
            tripCode: t(contact.tripCode),
            fishTag: t(contact.fishTag),
            licenseNumber: t(contact.licenseNumber),
            prepaidAmount: t(contact.prepaidAmount),
            subscribe_newsletter: Boolean(contact.subscribe_newsletter),
            termsAccepted: Boolean(contact.termsAccepted),
            updateCustomerFields: Boolean(contact.updateCustomerFields),
            licenseFrontImg: contact.licenseFrontImg ?? null,
            licenseBackImg: contact.licenseBackImg ?? null,
            licenseFileFront: contact.licenseFileFront instanceof File ? contact.licenseFileFront : null,
            licenseFileBack: contact.licenseFileBack instanceof File ? contact.licenseFileBack : null,
        },
        delivery: {
            method: String(delivery.method ?? "ship"),
            pickupLocationId: delivery.pickupLocationId ?? null,
            holdForPickup: Boolean(delivery.holdForPickup),
        },
        shipping: {
            shippingCustomerId: shipping.shippingCustomerId ?? null,
            defaultShipAddressId: shipping.defaultShipAddressId ?? null,
            attachAddressToCustomer: Boolean(shipping.attachAddressToCustomer),
            ...snapshotAddressFields(shipping),
        },
        billing: {
            useShippingAddress: Boolean(billing.useShippingAddress),
            billingCustomerId: billing.billingCustomerId ?? null,
            defaultBillAddressId: billing.defaultBillAddressId ?? null,
            attachAddressToCustomer: Boolean(billing.attachAddressToCustomer),
            ...snapshotAddressFields(billing),
        },
        dropoff: {
            locationId: dropoff.locationId ?? null,
            date: t(dropoff.date),
        },
        processing: {
            totalWeight: t(processing.totalWeight),
            containers: t(processing.containers),
            instructions: (processing.instructions ?? []).map((row) => ({
                speciesId: row?.speciesId ?? null,
                processId: row?.processId ?? null,
                quantity: Number(row?.quantity) || 0,
                unit: row?.unit ?? "Whole Fish",
                additionalNotes: t(row?.additionalNotes),
            })),
            options: normalizeOptions(processing.options),
        },
        signatureBlob: signatureBlob instanceof Blob ? signatureBlob : null,
    }
}

function stripBinaryRefs(snap) {
    return {
        ...snap,
        signatureBlob: null,
        contact: {
            ...snap.contact,
            licenseFileFront: null,
            licenseFileBack: null,
        },
    }
}

/**
 * True when edit-order form state differs from the post-prefill baseline.
 * @param {Parameters<typeof snapshotOrderFormDirtyState>[0]} current
 * @param {ReturnType<typeof snapshotOrderFormDirtyState> | null | undefined} baseline
 */
export function isOrderFormDirtyVsBaseline(current, baseline) {
    if (!baseline) {
        return false
    }
    const snap = snapshotOrderFormDirtyState(current)
    if (snap.signatureBlob !== baseline.signatureBlob) {
        return true
    }
    if (snap.contact.licenseFileFront !== baseline.contact.licenseFileFront) {
        return true
    }
    if (snap.contact.licenseFileBack !== baseline.contact.licenseFileBack) {
        return true
    }
    return JSON.stringify(stripBinaryRefs(snap)) !== JSON.stringify(stripBinaryRefs(baseline))
}
